import ThermostatIcon from '@mui/icons-material/Thermostat';
import { Alert, Box } from '@mui/material';
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useDeviceAlerts } from '@/api/customSensorAlerts/useDeviceAlerts';
import { useDevice } from '@/api/devices';
import { SeaSurfaceTemperatureItemFormattedWithUnits, useHistoricalSurfaceTemperatureData } from '@/api/historicalData';
import { usePreferredTimeZone } from '@/api/userAccount';
import { Line, LineChart, LineDataSlice, drawLineChart } from '@/charts';
import { colorsets } from '@/charts/common';
import { addRenderersByProcessingSource } from '@/charts/helpers';
import { AlertLine } from '@/charts/types';
import { SensorIcon } from '@/components/data/SensorIcon';
import {
    ChartDataDetails,
    ChartWithInfo,
    ProcessingSourceLegendItems,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { LineChartDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/LineChartDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useElementSize } from '@/hooks/useElementSize';
import { useTrackChartView } from '@/hooks/useIsViewedTracking';
import { useGlobalStore } from '@/store';
import { composeAlertLines } from '@/utils';

export const SEA_SURFACE_TEMPERATURE_LINE = {
    id: 'sea-surface-temperature',
    label: 'Surface temperature',
    valueKey: 'degrees' as const,
    color: colorsets.blue,
    unitKey: 'units' as const,
    unitTypeKey: 'unitType' as const,
};

export const LINES = [SEA_SURFACE_TEMPERATURE_LINE];

export type SeaSurfaceTemperatureChartProps = {
    chartTitle: string;
    spotterId: string;
    queryEnabled?: boolean;
    showAlertLines: boolean;
    scaleToFitAlerts: boolean;
    alertLines?: AlertLine[];
    lines: Line<SeaSurfaceTemperatureItemFormattedWithUnits>[];
    onSelectData?: (data: LineDataSlice<SeaSurfaceTemperatureItemFormattedWithUnits> | null) => void;
    onSelectTimestamp?: (timestamp: string | null, timeline?: string[]) => void;
};

export const SeaSurfaceTemperatureChart = ({
    chartTitle,
    spotterId,
    queryEnabled = true,
    showAlertLines,
    scaleToFitAlerts,
    alertLines,
    onSelectData,
    onSelectTimestamp,
    lines,
}: SeaSurfaceTemperatureChartProps) => {
    const id = useId();
    const { dateRange } = useDateRangeContext();
    const chartRef = useRef<LineChart<SeaSurfaceTemperatureItemFormattedWithUnits> | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
    const { selectedTimestamp } = useTimelineNavigationContext();
    const selectedTimestampRef = useRef(selectedTimestamp);
    selectedTimestampRef.current = selectedTimestamp;
    const timeZone = usePreferredTimeZone();

    const containerRef = useRef<HTMLElement>(null);
    const containerId = `sea-surface-temperature-chart${id}`;
    const { width, height } = useElementSize(containerRef);

    useTrackChartView({
        containerEl,
        spotterId,
        chartTitle,
    });

    const { data: seaSurfaceTemperatureData, isPending } = useHistoricalSurfaceTemperatureData({
        spotterId: spotterId!,
        dateRange,
        processingSources: 'all',
        config: { enabled: queryEnabled },
    });

    const drawChart = useCallback(drawLineChart, []);

    const linesWithRenderers = useMemo(
        () => addRenderersByProcessingSource(lines, seaSurfaceTemperatureData),
        [lines, seaSurfaceTemperatureData]
    );

    useEffect(() => {
        if (isPending) {
            return;
        }

        if (!seaSurfaceTemperatureData) {
            return;
        }

        const newChart = drawChart<SeaSurfaceTemperatureItemFormattedWithUnits>({
            dataset: seaSurfaceTemperatureData,
            lines: linesWithRenderers,
            xAxisKey: 'timestamp',
            yAxisLabel: 'Sea surface temperature',
            containerId,
            showAlertLines,
            scaleChartToFitAllAlerts: scaleToFitAlerts,
            alertLines,
            chartWidth: width,
            chartHeight: height,
            dateRange,
            timeZone,
            onSelectData,
            onSelectTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        alertLines,
        containerId,
        dateRange,
        timeZone,
        drawChart,
        height,
        isPending,
        linesWithRenderers,
        onSelectData,
        onSelectTimestamp,
        scaleToFitAlerts,
        seaSurfaceTemperatureData,
        showAlertLines,
        width,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    useLayoutEffect(() => {
        if (!containerRef.current) {
            return;
        }
        setContainerEl(containerRef.current);
    }, [seaSurfaceTemperatureData, dateRange]);

    return <Box ref={containerRef} id={containerId} width={1} height={1} />;
};

export const SeaSurfaceTemperatureSection = () => {
    const { setSelectedTimestamp } = useTimelineNavigationContext();
    const chartTitle = 'Sea surface temperature';
    const { data: device } = useDevice();
    const spotterId = device?.spotterId;
    const { dateRange } = useDateRangeContext();
    const isAlertLinesOn = useGlobalStore((state) => state.isAlertLinesOn);
    const isScaleToFitAlertLinesOn = useGlobalStore((state) => state.isScaleToFitAlertLinesOn);
    const [selectedData, setSelectedData] = useState<LineDataSlice<SeaSurfaceTemperatureItemFormattedWithUnits> | null>(
        null
    );

    const {
        data: seaSurfaceTemperatureData,
        isPending,
        isFetching,
    } = useHistoricalSurfaceTemperatureData({
        spotterId: spotterId!,
        dateRange,
        processingSources: 'all',
    });

    const { data: deviceAlerts } = useDeviceAlerts({
        config: { enabled: isAlertLinesOn },
    });

    const alertLines = useMemo(
        () => composeAlertLines({ deviceAlerts, triggerTypes: ['spotter_threshold_seasurfacetemperature'] }),
        [deviceAlerts]
    );

    if (!device?.externalHardware.surfaceTemperature && !device?.surfaceTemperature) {
        return;
    }

    return (
        <CollapsibleSection
            isFetching={isFetching}
            isPending={isPending}
            title={chartTitle}
            icon={<SensorIcon Icon={ThermostatIcon} />}
        >
            {seaSurfaceTemperatureData?.length ? (
                <Box>
                    <ChartWithInfo
                        legend={<ProcessingSourceLegendItems data={seaSurfaceTemperatureData} />}
                        chart={
                            <SeaSurfaceTemperatureChart
                                onSelectTimestamp={setSelectedTimestamp}
                                lines={LINES}
                                spotterId={spotterId!}
                                chartTitle={chartTitle}
                                onSelectData={setSelectedData}
                                showAlertLines={isAlertLinesOn}
                                scaleToFitAlerts={isScaleToFitAlertLinesOn}
                                alertLines={alertLines}
                            />
                        }
                    >
                        <ChartDataDetails>
                            {!selectedData && (
                                <LineChartDataSummary
                                    dateRange={dateRange}
                                    lines={LINES}
                                    dataset={seaSurfaceTemperatureData}
                                    renderLineSummary={() => (
                                        <>
                                            <DataLineMean />
                                            <DataLineExtent />
                                        </>
                                    )}
                                />
                            )}
                            <DataDetailsByTimestamp
                                selectedData={selectedData}
                                datumRenderer={({ lines, datum }) => (
                                    <LineChartDatumDetail
                                        dataset={seaSurfaceTemperatureData}
                                        datum={datum}
                                        lines={lines}
                                    />
                                )}
                            />
                        </ChartDataDetails>
                    </ChartWithInfo>
                </Box>
            ) : (
                <Alert severity="info">No sea surface temperature data for selected date range</Alert>
            )}
        </CollapsibleSection>
    );
};
